import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { can } from "@/lib/permissions";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  PageHeader,
  EmptyState,
  LoadingSkeleton,
  FormField,
} from "@/components/ui-system";
import { toast } from "sonner";
import {
  Plus,
  Star,
  Loader2,
  Layers,
  CheckCircle2,
  History as HistoryIcon,
} from "lucide-react";
import RateCardEditor from "./RateCardEditor";
import CourierAggregatorMapEditor from "./CourierAggregatorMapEditor";
import { PlanHistory } from "@/components/b2c/PlanHistory";

const inr = (n) =>
  typeof n === "number" ? `₹${new Intl.NumberFormat("en-IN").format(n)}` : "—";

const blankPlanForm = () => ({
  name: "",
  monthly_fee: "0",
  cod_cycle_days: "7",
  fee_refund_at_shipments: "",
});

/**
 * Count distinct couriers + total rows in a plan's rate card for the list summary.
 */
const planSummary = (plan) => {
  const rows = plan?.rate_card || [];
  const couriers = new Set(rows.map((r) => r.courier_code));
  return { couriers: couriers.size, rows: rows.length };
};

const B2CPlans = ({ user, onLogout }) => {
  const canManage = can(user, "b2c.plans:manage");
  const canManageCouriers = can(user, "b2c.couriers:manage");

  const [tab, setTab] = useLocalStorageState("admin-b2c-plans:tab", "plans");

  const [plans, setPlans] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState(blankPlanForm());
  const [creating, setCreating] = useState(false);

  const [togglingStatus, setTogglingStatus] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const selected = useMemo(
    () => plans.find((p) => p.id === selectedId) || null,
    [plans, selectedId]
  );

  const fetchAll = async () => {
    setLoading(true);
    setError(false);
    try {
      const [planRes, courierRes] = await Promise.all([
        axios.get(`${API}/b2c/admin/plans`),
        axios.get(`${API}/b2c/admin/couriers`),
      ]);
      const planList = planRes.data || [];
      setPlans(planList);
      setCouriers(courierRes.data || []);
      setSelectedId((prev) => {
        if (prev && planList.some((p) => p.id === prev)) return prev;
        return planList[0]?.id ?? null;
      });
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Refresh just the courier registry (after the Couriers tab edits one) so the
  // rate-card add-row picker stays current without reloading plans.
  const refreshCouriers = async () => {
    try {
      const res = await axios.get(`${API}/b2c/admin/couriers`);
      setCouriers(res.data || []);
    } catch (e) {
      /* non-fatal; surfaced on next full load */
    }
  };

  // Replace one plan in state after a save/import returns the updated doc.
  const applyPlanUpdate = (updated) => {
    if (!updated?.id) {
      fetchAll();
      return;
    }
    setPlans((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newForm.name.trim()) {
      toast.error("Plan name is required");
      return;
    }
    const cycle = parseInt(newForm.cod_cycle_days, 10);
    if (!(cycle >= 1)) {
      toast.error("COD cycle must be at least 1 day");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        name: newForm.name.trim(),
        monthly_fee: Number(newForm.monthly_fee) || 0,
        cod_cycle_days: cycle,
      };
      if (newForm.fee_refund_at_shipments !== "") {
        payload.fee_refund_at_shipments = Number(newForm.fee_refund_at_shipments);
      }
      const res = await axios.post(`${API}/b2c/admin/plans`, payload);
      toast.success("Plan created");
      setNewOpen(false);
      setNewForm(blankPlanForm());
      await fetchAll();
      if (res.data?.id) setSelectedId(res.data.id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
          : "Failed to create plan"
      );
    } finally {
      setCreating(false);
    }
  };

  const handleSetDefault = async () => {
    if (!selected) return;
    setSettingDefault(true);
    try {
      await axios.post(`${API}/b2c/admin/plans/${selected.id}/set-default`);
      toast.success(`"${selected.name}" is now the default plan`);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set default plan");
    } finally {
      setSettingDefault(false);
    }
  };

  const handleToggleEnabled = async () => {
    if (!selected) return;
    const next = selected.status === "active" ? "archived" : "active";
    setTogglingStatus(true);
    try {
      await axios.put(`${API}/b2c/admin/plans/${selected.id}`, { status: next });
      toast.success(next === "active" ? `"${selected.name}" enabled` : `"${selected.name}" disabled`);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update plan");
    } finally {
      setTogglingStatus(false);
    }
  };

  // ---- Plans tab body ------------------------------------------------------
  const renderPlansTab = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
          <LoadingSkeleton variant="detail" rows={5} />
        </div>
      );
    }
    if (error) {
      return (
        <div className="rounded-xl border bg-white p-8 text-center">
          <p className="text-sm text-slate-600">Could not load plans.</p>
          <Button variant="outline" className="mt-3" onClick={fetchAll} data-testid="plans-retry">
            Retry
          </Button>
        </div>
      );
    }
    if (plans.length === 0) {
      return (
        <EmptyState
          icon={Layers}
          title="No B2C plans yet"
          description="Create a plan, then import a rate-card CSV or add rows to start quoting B2C shipments."
          action={
            canManage ? (
              <Button onClick={() => setNewOpen(true)} data-testid="plans-new-empty">
                <Plus className="mr-2 h-4 w-4" />
                New plan
              </Button>
            ) : null
          }
        />
      );
    }

    return (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
        {/* Left: plan list */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Plans</h2>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setNewOpen(true)}
              disabled={!canManage}
              data-testid="plans-new"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              New
            </Button>
          </div>
          <div className="space-y-2">
            {plans.map((p) => {
              const sum = planSummary(p);
              const active = p.id === selectedId;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedId(p.id)}
                  data-testid={`plan-item-${p.id}`}
                  className={`w-full rounded-xl border bg-white p-4 text-left transition-colors ${
                    active ? "border-brand ring-1 ring-brand" : "hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-medium text-slate-800">{p.name}</span>
                    {p.is_default && (
                      <Badge className="shrink-0 bg-amber-100 text-amber-800 border-0 text-xs">
                        <Star className="mr-1 h-3 w-3 fill-amber-500 text-amber-500" />
                        Default
                      </Badge>
                    )}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        p.status === "active" ? "text-green-700" : "text-slate-500"
                      }`}
                    >
                      {p.status}
                    </Badge>
                    <span className="text-xs text-slate-500">v{p.version}</span>
                    <span className="text-xs text-slate-400">·</span>
                    <span className="text-xs text-slate-500 tabular-nums">
                      {sum.couriers} couriers / {sum.rows} rows
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: detail + rate-card editor */}
        <div className="min-w-0 space-y-6">
          {selected ? (
            <>
              <Card>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-xl font-semibold text-slate-800 truncate">{selected.name}</h2>
                        {selected.is_default && (
                          <Badge className="bg-amber-100 text-amber-800 border-0 text-xs">
                            <Star className="mr-1 h-3 w-3 fill-amber-500 text-amber-500" />
                            Default
                          </Badge>
                        )}
                        <Badge
                          variant="outline"
                          className={selected.status === "active" ? "text-green-700" : "text-slate-500"}
                        >
                          {selected.status === "active" ? "Enabled" : "Disabled"}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setHistoryOpen(true)}
                        data-testid="plan-history-open"
                      >
                        <HistoryIcon className="mr-1.5 h-4 w-4" />
                        History
                      </Button>
                      {!selected.is_default && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSetDefault}
                          disabled={!canManage || settingDefault || selected.status !== "active"}
                          data-testid="plan-set-default"
                        >
                          {settingDefault ? (
                            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                          ) : (
                            <Star className="mr-1.5 h-4 w-4" />
                          )}
                          Set as default
                        </Button>
                      )}
                      <div
                        className="flex items-center gap-2 rounded-lg border bg-slate-50 px-3 py-1.5"
                        title={selected.is_default ? "The default plan can't be disabled — set another plan as default first" : ""}
                      >
                        <Switch
                          checked={selected.status === "active"}
                          onCheckedChange={handleToggleEnabled}
                          disabled={!canManage || togglingStatus || selected.is_default}
                          data-testid="plan-toggle-enabled"
                        />
                        <span className="text-sm font-medium text-slate-600">
                          {selected.status === "active" ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Plan fields */}
                  <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">Monthly fee</dt>
                      <dd className="mt-0.5 text-sm font-semibold text-slate-800 tabular-nums">
                        {inr(selected.monthly_fee)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">COD cycle</dt>
                      <dd className="mt-0.5 text-sm font-semibold text-slate-800 tabular-nums">
                        {selected.cod_cycle_days} days
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">Fee refund at</dt>
                      <dd className="mt-0.5 text-sm font-semibold text-slate-800 tabular-nums">
                        {selected.fee_refund_at_shipments != null
                          ? `${new Intl.NumberFormat("en-IN").format(selected.fee_refund_at_shipments)} shipments`
                          : "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">Version</dt>
                      <dd className="mt-0.5 text-sm font-semibold text-slate-800 tabular-nums">v{selected.version}</dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>

              {/* Rate-card editor */}
              <Card>
                <CardContent className="p-4 sm:p-5">
                  <RateCardEditor
                    plan={selected}
                    couriers={couriers}
                    canManage={canManage}
                    onSaved={applyPlanUpdate}
                  />
                </CardContent>
              </Card>
            </>
          ) : (
            <EmptyState
              icon={CheckCircle2}
              title="Select a plan"
              description="Pick a plan on the left to view and edit its rate card."
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <PageHeader
          title="B2C Plans & Rate Cards"
          subtitle="Billing plans, zone-based rate cards (A–F), and the canonical courier registry."
        />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="b2c-plans-tabs">
            <TabsTrigger value="plans" data-testid="tab-plans">
              Plans
            </TabsTrigger>
            <TabsTrigger value="couriers" data-testid="tab-couriers">
              Couriers
            </TabsTrigger>
          </TabsList>

          <TabsContent value="plans" className="mt-6">
            {renderPlansTab()}
          </TabsContent>

          <TabsContent value="couriers" className="mt-6">
            <CourierAggregatorMapEditor canManage={canManageCouriers} onCouriersChanged={refreshCouriers} />
          </TabsContent>
        </Tabs>
      </div>

      {/* New Plan dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New B2C plan</DialogTitle>
            <DialogDescription>
              Creates an empty plan. Add rate-card rows or import a CSV afterwards.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <FormField label="Plan name" htmlFor="new-plan-name" required>
              <Input
                id="new-plan-name"
                value={newForm.name}
                onChange={(e) => setNewForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="new-plan-name"
              />
            </FormField>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Monthly fee (₹)" htmlFor="new-plan-fee">
                <Input
                  id="new-plan-fee"
                  type="number"
                  step="any"
                  min="0"
                  value={newForm.monthly_fee}
                  onChange={(e) => setNewForm((f) => ({ ...f, monthly_fee: e.target.value }))}
                  data-testid="new-plan-fee"
                />
              </FormField>
              <FormField label="COD cycle (days)" htmlFor="new-plan-cycle" required>
                <Input
                  id="new-plan-cycle"
                  type="number"
                  min="1"
                  value={newForm.cod_cycle_days}
                  onChange={(e) => setNewForm((f) => ({ ...f, cod_cycle_days: e.target.value }))}
                  data-testid="new-plan-cycle"
                />
              </FormField>
            </div>
            <FormField
              label="Fee refund at shipments"
              htmlFor="new-plan-refund"
              hint="Optional — monthly fee is refunded once the seller crosses this shipment count."
            >
              <Input
                id="new-plan-refund"
                type="number"
                min="0"
                value={newForm.fee_refund_at_shipments}
                onChange={(e) => setNewForm((f) => ({ ...f, fee_refund_at_shipments: e.target.value }))}
                placeholder="Leave blank for none"
                data-testid="new-plan-refund"
              />
            </FormField>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewOpen(false)} data-testid="new-plan-cancel">
                Cancel
              </Button>
              <Button type="submit" disabled={creating} data-testid="new-plan-submit">
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create plan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Plan change history */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Change history{selected ? ` — ${selected.name}` : ""}</DialogTitle>
            <DialogDescription>
              Every edit to this plan and its rate card, newest first — who, when, and what changed.
            </DialogDescription>
          </DialogHeader>
          {selected && historyOpen && <PlanHistory planId={selected.id} />}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default B2CPlans;
